import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import './Dashboard.scss';

const EmployeeDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [keywordInput, setKeywordInput] = useState('');
  const [keywordChips, setKeywordChips] = useState<string[]>([]);
  const [showDetailedSearch, setShowDetailedSearch] = useState(false);

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="employee-dashboard">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated or not an employee
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker') {
    return <Navigate to="/" replace />;
  }

  // Handle keyword input - create chips when Space or Enter is pressed
  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    console.log('🔑 Key pressed:', e.key, 'Input:', keywordInput);
    
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      
      if (keywordInput.trim()) {
        const newKeywords = keywordInput.trim().split(/\s+/);
        const uniqueKeywords = Array.from(new Set([...keywordChips, ...newKeywords]));
        console.log('✅ Creating chips:', uniqueKeywords);
        setKeywordChips(uniqueKeywords);
        setKeywordInput('');
      }
    }
  };

  // Remove a keyword chip
  const removeKeywordChip = (keyword: string) => {
    setKeywordChips(keywordChips.filter(k => k !== keyword));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const allKeywords = [...keywordChips];
    if (keywordInput.trim()) {
      allKeywords.push(...keywordInput.trim().split(/\s+/));
    }
    if (allKeywords.length > 0) {
      const params = new URLSearchParams();
      params.set('q', allKeywords.join(' '));
      params.set('keywords', allKeywords.join(','));
      window.location.href = `/darbo-skelbimai?${params.toString()}`;
    }
  };

  const handleDetailedSearch = () => {
    setShowDetailedSearch(!showDetailedSearch);
  };

  const getUserName = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.email;
  };

  return (
    <div className="employee-dashboard">
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="container">
          <div className="hero-content">
            <div className="welcome-message">
              <h1>Sveiki, {getUserName()}!</h1>
              <p className="hero-subtitle">Raskite savo idealų darbą</p>
            </div>

            {/* SEARCH FORM */}
            <div className="search-form-container">
              <form className="main-search-form" onSubmit={handleSearch}>
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Įrašyk kokio darbo ieškai"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                  />
                  <button type="submit" className="search-btn" disabled={keywordChips.length === 0 && !keywordInput.trim()}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                    IEŠKOTI
                  </button>
                </div>

                {/* Display keyword chips */}
                {keywordChips.length > 0 && (
                  <div className="keyword-chips">
                    {keywordChips.map((keyword) => (
                      <button
                        key={keyword}
                        type="button"
                        className="keyword-chip"
                        onClick={() => removeKeywordChip(keyword)}
                      >
                        <span>{keyword}</span>
                        <span className="remove-chip">×</span>
                      </button>
                    ))}
                  </div>
                )}
              </form>

              <button
                className="detailed-search-toggle"
                onClick={handleDetailedSearch}
              >
                {showDetailedSearch ? 'Paslėpti išsamią paiešką' : 'Išsami paieška'}
              </button>

              {/* DETAILED SEARCH FORM */}
              {showDetailedSearch && (
                <div className="detailed-search-form">
                  <div className="filters-header">
                    <div className="search-icon-circle">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                      </svg>
                    </div>
                    <h2>Detali paieška</h2>
                  </div>

                  <div className="filters-grid">
                    {/* LEFT COLUMN */}
                    <div className="filter-card">
                      <div className="card-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        <span>Darbo vietos</span>
                      </div>
                      <button type="button" className="select-button">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14"></path>
                          <path d="M12 5v14"></path>
                        </svg>
                        PASIRINKTI
                      </button>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="filter-card">
                      <div className="card-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                          <rect width="20" height="14" x="2" y="6" rx="2"></rect>
                        </svg>
                        <span>Darbo sritys</span>
                      </div>
                      <button type="button" className="select-button">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14"></path>
                          <path d="M12 5v14"></path>
                        </svg>
                        PASIRINKTI
                      </button>
                    </div>

                    {/* LEFT COLUMN */}
                    <div className="filter-card">
                      <div className="card-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                          <path d="M10 6h4"></path>
                          <path d="M10 10h4"></path>
                          <path d="M10 14h4"></path>
                          <path d="M10 18h4"></path>
                        </svg>
                        <span>Įmonių veiklos sritys / Įmonės</span>
                      </div>
                      <input
                        type="text"
                        placeholder="Įveskite įmonės pavadinimą ar sritį"
                        className="card-input"
                      />
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="filter-card">
                      <div className="card-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M12 6v6l4 2"></path>
                        </svg>
                        <span>Darbo tipas ir darbo laikas</span>
                      </div>
                      <button type="button" className="select-button">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14"></path>
                          <path d="M12 5v14"></path>
                        </svg>
                        PASIRINKTI
                      </button>
                    </div>

                    {/* LEFT COLUMN */}
                    <div className="filter-card">
                      <div className="card-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.25M15.54 15.54l4.24 4.25M1 12h6M17 12h6M4.22 19.78l4.24-4.25M15.54 8.46l4.24-4.25"></path>
                        </svg>
                        <span>Skelbimo parametrai</span>
                      </div>
                      <button type="button" className="select-button">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14"></path>
                          <path d="M12 5v14"></path>
                        </svg>
                        PASIRINKTI
                      </button>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="filter-card">
                      <div className="card-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18.5 21a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"></path>
                          <path d="M5.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"></path>
                          <path d="M2 22h7M15 22h7M9 7.5h6M9 17.5h6"></path>
                        </svg>
                        <span>Atlyginimas</span>
                      </div>
                      <input
                        type="number"
                        placeholder="Atlyginimas (netto) EUR, nuo"
                        className="card-input"
                        min="0"
                        step="100"
                      />
                    </div>
                  </div>

                  <div className="filters-footer">
                    <button type="button" className="show-results-button">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      RODYTI SKELBIMUS
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* POPULAR SEARCHES */}
            <div className="popular-searches">
              <p>Populiarios paieškos:</p>
              <div className="search-tags">
                <button className="search-tag">IT specialistas</button>
                <button className="search-tag">Pardavimų vadybininkas</button>
                <button className="search-tag">Buhalteris</button>
                <button className="search-tag">Dizaineris</button>
                <button className="search-tag">Projektų vadovas</button>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default EmployeeDashboard;
